import { FC, useState, useCallback, useMemo } from "react";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import Button from "@mui/material/Button";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddIcon from "@mui/icons-material/Add";
import { BuyersJourneyCheckboxList } from "@/lib/company-filter/modules/intent/components/filters/BuyersJourneyCheckboxList";
import { useGetIntentBuyersJourneyQuery } from "@/generated/taxonomy";
import { TAXONOMY_CLIENT } from "@/utils/consts";
import type {
  ScoringType,
  BuyersJourneyBand,
  TopicsFilter,
} from "@/lib/scoring/types";
import type { CachedTopic } from "@/types/intent";
import { IntentTopicsSection } from "./IntentTopicsSection";

const DEFAULT_TOPICS: TopicsFilter = {
  ids: [],
  inclusionMethod: "ANY_PRESENT",
};

// ── Exports for criterion registry ────────────────────────────────────────

export const initialBand: BuyersJourneyBand = {
  buyersJourneyIds: [],
  score: 0,
  topics: DEFAULT_TOPICS,
};

export function hasErrors(bands: unknown[]): boolean {
  const typed = bands as BuyersJourneyBand[];
  if (typed.length === 0) return true;
  const topicIds = typed[0]?.topics?.ids ?? [];
  if (topicIds.length === 0) return true;
  return typed.some((band, i) => {
    const others = typed.filter((_, j) => j !== i);
    return getBandError(band, others) !== null;
  });
}

export function getBandSummary(band: unknown): string {
  const b = band as BuyersJourneyBand;
  const count = b.buyersJourneyIds?.length ?? 0;
  if (count === 0) return "—";
  return `${count} journey stage${count !== 1 ? "s" : ""}`;
}

// ── Validation ────────────────────────────────────────────────────────────

function getBandError(
  band: BuyersJourneyBand,
  others: BuyersJourneyBand[]
): string | null {
  if (!band.score) return "Points can't be empty";
  if (!band.buyersJourneyIds?.length)
    return "Select at least one journey stage";

  const otherIds = new Set(others.flatMap((o) => o.buyersJourneyIds ?? []));
  const hasDuplicate = (band.buyersJourneyIds ?? []).some((id) =>
    otherIds.has(id)
  );
  if (hasDuplicate) return "Journey stages can't be the same across bands";

  return null;
}

// ── Component ─────────────────────────────────────────────────────────────

interface Props {
  criterion: ScoringType;
  onChange: (updated: ScoringType) => void;
}

export const BuyersJourneyEditor: FC<Props> = ({ criterion, onChange }) => {
  const bands = criterion.buyersJourney ?? [];

  const [topicsCache, setTopicsCache] = useState<CachedTopic[]>([]);
  const [topics, setTopics] = useState<TopicsFilter>(
    bands[0]?.topics ?? DEFAULT_TOPICS
  );

  // Fetch journey stages to check cardinality for "Add Band" button
  const { data: journeysData } = useGetIntentBuyersJourneyQuery({
    context: { clientName: TAXONOMY_CLIENT },
  });
  const totalStages = useMemo(
    () =>
      (journeysData?.intentBuyersJourney?.data ?? []).filter((j) => j?.id)
        .length,
    [journeysData]
  );

  // All stages are selected when every unique stage is covered across bands
  const allStagesSelected =
    totalStages > 0 &&
    new Set(bands.flatMap((b) => b.buyersJourneyIds ?? [])).size >= totalStages;

  const handleTopicsChange = useCallback(
    (newTopics: TopicsFilter, cache: CachedTopic[]) => {
      setTopics(newTopics);
      setTopicsCache(cache);
      onChange({
        ...criterion,
        buyersJourney: bands.map((b) => ({ ...b, topics: newTopics })),
      });
    },
    [bands, criterion, onChange]
  );

  const updateBand = (index: number, patch: Partial<BuyersJourneyBand>) => {
    onChange({
      ...criterion,
      buyersJourney: bands.map((b, i) =>
        i === index ? { ...b, ...patch, topics } : { ...b, topics }
      ),
    });
  };

  const removeBand = (index: number) => {
    onChange({
      ...criterion,
      buyersJourney: bands
        .filter((_, i) => i !== index)
        .map((b) => ({ ...b, topics })),
    });
  };

  const addBand = () => {
    onChange({
      ...criterion,
      buyersJourney: [
        ...bands.map((b) => ({ ...b, topics })),
        { buyersJourneyIds: [], score: 0, topics },
      ],
    });
  };

  return (
    <Box sx={{ px: 2 }}>
      <IntentTopicsSection
        topics={topics}
        topicsCache={topicsCache}
        onChange={handleTopicsChange}
      />

      {bands.map((band, i) => {
        const others = bands.filter((_, j) => j !== i);
        const isDirty =
          (band.buyersJourneyIds?.length ?? 0) > 0 || band.score !== 0;
        const error = isDirty ? getBandError(band, others) : null;
        return (
          <Box
            key={i}
            sx={{
              border: "1px solid",
              borderColor: error ? "error.main" : "divider",
              borderRadius: 1,
              p: 1.5,
              mb: 1,
            }}
          >
            <Stack
              direction="row"
              alignItems="center"
              spacing={0.5}
              sx={{ mb: 1 }}
            >
              <Typography variant="body2" fontWeight={600}>
                Buyer's Journey
              </Typography>
              <Tooltip
                title="Select the buyer's journey stages to score. Hover over each option to see its description."
                placement="top"
              >
                <InfoOutlinedIcon
                  sx={{ fontSize: 16, color: "text.secondary" }}
                />
              </Tooltip>
            </Stack>
            <BuyersJourneyCheckboxList
              selectedIds={band.buyersJourneyIds ?? []}
              onChange={(ids) => updateBand(i, { buyersJourneyIds: ids })}
            />

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-end",
                gap: 1,
                mt: 1,
              }}
            >
              <TextField
                size="small"
                type="number"
                label="Pts"
                error={band.score === 0 && isDirty}
                value={band.score === 0 ? "" : band.score}
                onChange={(e) =>
                  updateBand(i, {
                    score: e.target.value === "" ? 0 : Number(e.target.value),
                  })
                }
                inputProps={{ min: 0, step: 1 }}
                sx={{ width: 80 }}
              />
              <IconButton
                size="small"
                onClick={() => removeBand(i)}
                aria-label="Remove band"
              >
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Box>

            {error && (
              <Typography
                variant="caption"
                color="error"
                sx={{ display: "block", mt: 0.5 }}
              >
                {error}
              </Typography>
            )}
          </Box>
        );
      })}

      {!allStagesSelected && (
        <Button
          size="small"
          startIcon={<AddIcon />}
          onClick={addBand}
          sx={{ color: "primary.main", fontWeight: 600 }}
        >
          ADD BAND
        </Button>
      )}
    </Box>
  );
};
